/**
 * A screwdriver: the first thing small enough to go in a pocket.
 *
 * Physically it is a box like any other carryable — same dynamic body, same registration,
 * same `userData` for the interaction ray — so picking it up, carrying it and throwing it
 * all work through the paths that already exist. What makes it different is its kind:
 * `OBJECT_KINDS.screwdriver.stowable` is what lets it be stowed (design D1).
 *
 * The collider is the item's whole box rather than the shaft's actual shape. A 4 cm tool
 * on a table is hard to put a crosshair on, and a generous collider is the cheap fix the
 * design names for that — geometry, not a new rule.
 */

import { CuboidCollider, RigidBody, type RapierRigidBody } from '@react-three/rapier';
import { useEffect, useRef } from 'react';
import { BackSide } from 'three';
import type { PlacedObject } from '../game/profile/buildGeometry';
import { useGameStore } from '../store/gameStore';
import { boxPosition, halfExtents } from './boxUnits';
import { getGradientMap } from './materials';
import { CARRYABLE_GROUPS, registerObjectBody, unregisterObjectBody } from './objectRegistry';
import type { BodyUserData } from './useInteractionTarget';

const HANDLE_COLOR = '#c1442e';
const SHAFT_COLOR = '#b9bec6';

type Props = {
  object: PlacedObject;
};

export function Screwdriver({ object }: Props) {
  const bodyRef = useRef<RapierRigidBody>(null);
  const isFocused = useGameStore((store) => store.focus?.id === object.id);
  // In the pocket: still mounted and still registered, but switched off and out of sight
  // (design D3). A discrete subscription, like focus — it changes on a stow and at no
  // other time.
  const isStowed = useGameStore((store) =>
    store.pocket.some((item) => item?.id === object.id),
  );

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    registerObjectBody(object.id, body);
    return () => unregisterObjectBody(object.id);
  }, [object.id]);

  const { size } = object.box;
  // Handle at the back, shaft running forward: the long axis is z, the way the kind's
  // size is written.
  const handleLength = size.z * 0.4;
  const shaftLength = size.z - handleLength;
  const emissive = isFocused ? HANDLE_COLOR : '#000000';
  const emissiveIntensity = isFocused ? 0.45 : 0;

  return (
    <RigidBody
      ref={bodyRef}
      colliders={false}
      position={boxPosition(object.box)}
      // Thrown, it is small and fast: CCD is what keeps it from passing through a hedge
      // in one physics step.
      ccd
      userData={
        // The kind travels with the body so the interaction ray can report it: it is what
        // tells `availableAction` whether what is held fits what is looked at (design D5).
        { kind: 'carryable', id: object.id, objectKind: object.kind } satisfies BodyUserData
      }
    >
      <CuboidCollider args={halfExtents(object.box)} collisionGroups={CARRYABLE_GROUPS} />
      <group visible={!isStowed}>
        <mesh position={[0, 0, -(size.z - handleLength) / 2]} castShadow receiveShadow>
          <boxGeometry args={[size.x, size.y, handleLength]} />
          <meshToonMaterial
            color={HANDLE_COLOR}
            gradientMap={getGradientMap()}
            emissive={emissive}
            emissiveIntensity={emissiveIntensity}
          />
        </mesh>
        <mesh position={[0, 0, handleLength / 2]} rotation={[Math.PI / 2, 0, 0]} castShadow>
          <cylinderGeometry args={[size.x * 0.18, size.x * 0.18, shaftLength, 8]} />
          <meshToonMaterial
            color={SHAFT_COLOR}
            gradientMap={getGradientMap()}
            emissive={isFocused ? SHAFT_COLOR : '#000000'}
            emissiveIntensity={emissiveIntensity}
          />
        </mesh>
        {isFocused && (
          // Bigger than the box-outline scale a cardboard box uses: 8% of 4 cm is not a
          // visible outline, and this is the item most in need of one.
          <mesh scale={[2.2, 2.2, 1.1]}>
            <boxGeometry args={[size.x, size.y, size.z]} />
            <meshBasicMaterial color="#ffffff" side={BackSide} />
          </mesh>
        )}
      </group>
    </RigidBody>
  );
}
